angular.module('createReferrals')
    .factory('ReferralAutosave', ['$http', '$q', '$timeout', 'API_ENDPOINT',
        function ($http, $q, $timeout, API_ENDPOINT) {

            var DEBOUNCE_MS = 1000;

            // The lazy draft create never carries notes (DCL-636). The autosave
            // PUT that follows it sends the same notes, so a create with notes
            // inserted each of them twice; notes are saved by the PUT alone.
            function withoutNotes(payload) {
                var copy = angular.extend({}, payload);
                if (payload && payload.referral) {
                    copy.referral = angular.extend({}, payload.referral);
                    delete copy.referral.notes_attributes;
                }
                return copy;
            }

            // One autosave session per create/review screen instance (DCL-369).
            //
            // config = {
            //   buildPayload:   function() -> {referral: {...}, patient: {...}?}
            //   createDraft:    function(payload) -> promise resolving to {id, draft_edit_token?}
            //                   The payload carries no notes (DCL-636). Omit when the
            //                   screen starts with an existing referral id.
            //   referralId:     existing referral id or null
            //   canCreateDraft: function() -> bool — guest flow: identity ready. Optional.
            //   guestToken:     function() -> token or null. Optional.
            //   onState:        function(state) — 'idle'|'saving'|'saved'|'failed'
            //   onSaved:        function(responseData, sentPayload) — after each successful PUT
            //   onDraftCreated: function(referralId) — once, right after lazy creation. Optional.
            // }
            function createSession(config) {
                var session = {};
                var referralId = config.referralId || null;
                var debounceTimer = null;
                var inFlight = null;      // promise of the current save request
                var dirty = false;        // changes exist that are newer than the last successful save
                var lastSent = null;      // JSON of the last successfully saved payload
                var createPromise = null; // in-flight lazy draft creation
                var state = 'idle';

                function setState(newState) {
                    if (newState === state) { return; }
                    state = newState;
                    if (config.onState) { config.onState(newState); }
                }

                function headers() {
                    var token = config.guestToken && config.guestToken();
                    return token ? {'X-Draft-Edit-Token': token} : {};
                }

                function readyToCreate() {
                    return !config.canCreateDraft || config.canCreateDraft();
                }

                function ensureDraft() {
                    if (referralId) { return $q.resolve(referralId); }
                    if (createPromise) { return createPromise; }
                    createPromise = config.createDraft(withoutNotes(config.buildPayload())).then(function (created) {
                        referralId = created.id;
                        if (config.onDraftCreated) { config.onDraftCreated(referralId); }
                        return referralId;
                    }).finally(function () {
                        createPromise = null;
                    });
                    return createPromise;
                }

                function doSave() {
                    var payload = config.buildPayload();
                    var serialized = angular.toJson(payload);
                    if (referralId && serialized === lastSent) {
                        dirty = false;
                        setState('saved');
                        return $q.resolve();
                    }
                    setState('saving');
                    dirty = false;
                    var creating = !referralId;
                    inFlight = ensureDraft().then(function (id) {
                        if (creating) {
                            // Built again now that the draft exists: the guest
                            // user who authors the notes is made by the create.
                            payload = config.buildPayload();
                            serialized = angular.toJson(payload);
                        }
                        return $http.put(API_ENDPOINT + '/referrals/' + id, payload,
                                         {headers: headers(), skipSpinner: true});
                    }).then(function (response) {
                        lastSent = serialized;
                        if (config.onSaved) { config.onSaved(response.data, payload); }
                        if (dirty) {
                            schedule();
                        } else {
                            setState('saved');
                        }
                    }).catch(function (error) {
                        dirty = true;
                        setState('failed');
                        return $q.reject(error);
                    }).finally(function () {
                        inFlight = null;
                    });
                    return inFlight;
                }

                function schedule() {
                    if (debounceTimer) { $timeout.cancel(debounceTimer); }
                    debounceTimer = $timeout(function () {
                        debounceTimer = null;
                        if (inFlight) { return; } // doSave re-schedules when it finishes dirty
                        doSave();
                    }, DEBOUNCE_MS);
                }

                // A watched form value changed.
                session.notifyChange = function () {
                    dirty = true;
                    if (!referralId && !readyToCreate()) { return; } // guest identity not ready: keep local
                    // 'saving' covers the whole pending window (debounce + request), so the
                    // indicator never claims "saved" while changes are still unsaved (DCL-411)
                    setState('saving');
                    schedule();
                };

                // Save now (blur, navigation, before Send). Resolves when nothing is pending.
                session.flush = function () {
                    if (debounceTimer) { $timeout.cancel(debounceTimer); debounceTimer = null; }
                    if (inFlight) {
                        return inFlight.then(function () { return session.flush(); });
                    }
                    if (!dirty) { return $q.resolve(); }
                    if (!referralId && !readyToCreate()) { return $q.resolve(); }
                    return doSave();
                };

                session.retry = function () { return session.flush(); };

                session.hasPendingChanges = function () {
                    return !!(dirty || debounceTimer || inFlight || state === 'failed');
                };

                // A save request is open: the lazy create or the PUT (DCL-636).
                // Not the debounce window and not the 'saving' state, which spans
                // it, so the note controls disabled on this don't grey out after
                // every keystroke elsewhere on the form.
                session.isRequestInFlight = function () {
                    return !!(inFlight || createPromise);
                };

                session.referralId = function () { return referralId; };

                // Restore path (DCL-440): seed an already-persisted draft id when a
                // reloaded screen re-hydrates from the server. Never overwrites an
                // existing id and fires no callbacks, so it can't trigger a create.
                session.adoptDraft = function (id) {
                    if (!referralId) { referralId = id; }
                };

                // First attachment add: the upload awaits the draft id.
                session.ensureDraftNow = function () {
                    if (referralId) { return $q.resolve(referralId); }
                    if (!readyToCreate()) { return $q.reject('draft.not.ready'); }
                    return ensureDraft();
                };

                return session;
            }

            return {createSession: createSession, withoutNotes: withoutNotes};
        }]);
